// components/dialogs/ProgramSettingsDialog.jsx
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Separator } from "../ui/separator";
import { supabase } from '../../lib/supabase';
import { useToast } from "../ui/use-toast";

const ProgramSettingsDialog = ({ isOpen, onClose, userPreferences, onUpdate }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [programSettings, setProgramSettings] = useState({
    goal_type: userPreferences?.goal_type || 'maintenance',
    deficit_cal_amount: userPreferences?.deficit_cal_amount || 500,
    surplus_cal_amount: userPreferences?.surplus_cal_amount || 500,
    protein_multiplier: userPreferences?.protein_multiplier || 1.0,
    fat_multiplier: userPreferences?.fat_multiplier || 0.4
  });
  const { toast } = useToast();

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('user_preferences')
        .update({
          ...programSettings,
          updated_at: new Date().toISOString()
        })
        .eq('id', userPreferences.id);

      if (error) throw error;

      toast({
        title: "Program Updated",
        description: "Your program settings have been updated successfully."
      });

      onUpdate();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update program settings",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Program Settings</DialogTitle>
          <DialogDescription>
            Update your nutrition program settings and macro targets
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label>Program Type</Label>
            <RadioGroup
              value={programSettings.goal_type}
              onValueChange={(value) => setProgramSettings({
                ...programSettings,
                goal_type: value
              })}
              className="space-y-1"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deficit" id="deficit" />
                <Label htmlFor="deficit">Cut (Calorie Deficit)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="maintenance" id="maintenance" />
                <Label htmlFor="maintenance">Maintenance</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="surplus" id="surplus" />
                <Label htmlFor="surplus">Bulk (Calorie Surplus)</Label>
              </div>
            </RadioGroup>
          </div>

          {programSettings.goal_type === 'deficit' && (
            <div className="space-y-2">
              <Label>Deficit Amount (calories)</Label>
              <Input
                type="number"
                value={programSettings.deficit_cal_amount}
                onChange={(e) => setProgramSettings({
                  ...programSettings,
                  deficit_cal_amount: parseInt(e.target.value)
                })}
              />
              <p className="text-xs text-muted-foreground">
                Recommended range: 250-750 calories
              </p>
            </div>
          )}

          {programSettings.goal_type === 'surplus' && (
            <div className="space-y-2">
              <Label>Surplus Amount (calories)</Label>
              <Input
                type="number"
                value={programSettings.surplus_cal_amount}
                onChange={(e) => setProgramSettings({
                  ...programSettings,
                  surplus_cal_amount: parseInt(e.target.value)
                })}
              />
              <p className="text-xs text-muted-foreground">
                Recommended range: 250-500 calories
              </p>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <Label>Protein (g/lb of lean mass)</Label>
            <Input
              type="number"
              step="0.1"
              value={programSettings.protein_multiplier}
              onChange={(e) => setProgramSettings({
                ...programSettings,
                protein_multiplier: parseFloat(e.target.value)
              })}
            />
            <p className="text-xs text-muted-foreground">
              Recommended range: 0.8-1.2g per pound of lean mass
            </p>
          </div>

          <div className="space-y-2">
            <Label>Fat (g/lb of lean mass)</Label>
            <Input
              type="number"
              step="0.1"
              value={programSettings.fat_multiplier}
              onChange={(e) => setProgramSettings({
                ...programSettings,
                fat_multiplier: parseFloat(e.target.value)
              })}
            />
            <p className="text-xs text-muted-foreground">
              Recommended range: 0.3-0.5g per pound of lean mass
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProgramSettingsDialog;